/**
 * Layer 3: VelaCrossValidationDrawer (Advisory Consumer Drawer)
 * 
 * Invariants:
 * 1. Connects Trade Advisory Blueprint with MarketValidationLensModal & MarketValidationChart.
 * 2. Fetches 100 canonical candles via apiClient.getCanonicalCandles.
 * 3. Generation / Cancellation token invalidation on close, symbol switch, timeframe switch, and unmount.
 * 4. Priority 1~5 Ordered Freshness Banner (DISCONNECTED / LOADING / EMPTY / STALE / FRESH).
 * 5. Discrete TD9 setup alignment to exact blueprint.close_time_utc.
 * 6. Pure Decimal presentation values formatted via formatFinancialPrice().
 * 7. Snapshot Binding & Tactical Layer Muting: Suppresses geometry in LOADING state; applies
 *    opacity muting and pointer lock in STALE/DISCONNECTED states.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  apiClient,
  CanonicalCandleV1,
  TradeAdvisorySymbolBlueprintV1,
  CadenceStatusV1,
} from '../../../api/client';
import { MarketValidationLensModal } from '../../../shared/components/modal/MarketValidationLensModal';
import { MarketValidationChart } from '../../../shared/components/chart/MarketValidationChart';
import {
  TimeframeId,
  mapCanonicalCandlesToChart,
  extractTradeGeometry,
  matchTdAnchor,
  evaluateDualSourceFreshness,
  formatFinancialPrice,
} from '../advisoryValidationAdapter';

export interface VelaCrossValidationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  symbol: string;
  timeframe: TimeframeId;
  blueprint: TradeAdvisorySymbolBlueprintV1 | null | undefined;
  cadenceStatus?: CadenceStatusV1 | null;
  onSelectTimeframe?: (timeframe: TimeframeId) => void;
}

const AVAILABLE_TIMEFRAMES: TimeframeId[] = ['5m', '15m', '30m', '1h', '4h', '1d'];

export const VelaCrossValidationDrawer: React.FC<VelaCrossValidationDrawerProps> = ({
  isOpen,
  onClose,
  symbol,
  timeframe: initialTimeframe,
  blueprint,
  cadenceStatus,
  onSelectTimeframe,
}) => {
  const [activeTimeframe, setActiveTimeframe] = useState<TimeframeId>(initialTimeframe);
  const [candles, setCandles] = useState<CanonicalCandleV1[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasNetworkError, setHasNetworkError] = useState<boolean>(false);
  const reqGenRef = useRef<number>(0);

  // Synchronize initial timeframe when prop changes
  useEffect(() => {
    setActiveTimeframe(initialTimeframe);
  }, [initialTimeframe]);

  // Local 1-second clock ticker to keep time-dependent freshness and expiry strictly evaluated
  const [clockMs, setClockMs] = useState<number>(() => Date.now());

  useEffect(() => {
    if (!isOpen) return;
    setClockMs(Date.now());
    const timer = setInterval(() => {
      setClockMs(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen]);

  // Fetch candles with Generation Token protection
  const fetchCandles = useCallback(
    async (targetSymbol: string, targetTf: TimeframeId) => {
      const currentGen = ++reqGenRef.current;
      setIsLoading(true);
      setHasNetworkError(false);

      try {
        const response = await apiClient.getCanonicalCandles(targetSymbol, targetTf, 100, 'SPOT');
        if (currentGen === reqGenRef.current) {
          // Verify response format, symbol and timeframe identity
          const isSymbolMatch = !response.symbol || response.symbol.toUpperCase() === targetSymbol.toUpperCase();
          const isTfMatch = !response.timeframe || response.timeframe === targetTf;
          if (response.format === 'canonical' && Array.isArray(response.candles) && isSymbolMatch && isTfMatch) {
            setCandles(response.candles);
          } else {
            setCandles([]);
          }
          setIsLoading(false);
        }
      } catch {
        if (currentGen === reqGenRef.current) {
          setCandles([]);
          setHasNetworkError(true);
          setIsLoading(false);
        }
      }
    },
    []
  );

  // Invalidate and refetch on symbol/timeframe change or open
  useEffect(() => {
    if (isOpen && symbol) {
      fetchCandles(symbol, activeTimeframe);
    } else {
      reqGenRef.current++;
      setCandles([]);
      setIsLoading(false);
    }

    return () => {
      reqGenRef.current++;
    };
  }, [isOpen, symbol, activeTimeframe, fetchCandles]);

  const handleTimeframeChange = (tf: TimeframeId) => {
    setActiveTimeframe(tf);
    if (onSelectTimeframe) {
      onSelectTimeframe(tf);
    }
  };

  // 1. Stable candle mapping and geometry extraction (pure data transforms, NOT bound to clockMs)
  const chartCandles = useMemo(() => {
    return mapCanonicalCandlesToChart(candles);
  }, [candles]);

  const tradeGeometry = useMemo(() => {
    return extractTradeGeometry(blueprint);
  }, [blueprint]);

  const tdMarker = useMemo(() => {
    return matchTdAnchor(candles, blueprint);
  }, [candles, blueprint]);

  // 2. Dynamic dual-source freshness evaluation (bound to local clock ticker)
  const freshness = useMemo(() => {
    return evaluateDualSourceFreshness({
      candles,
      blueprint,
      timeframe: activeTimeframe,
      nowMs: clockMs,
      isLoading,
      hasNetworkError,
      cadenceStatus,
    });
  }, [candles, blueprint, activeTimeframe, clockMs, isLoading, hasNetworkError, cadenceStatus]);


  // Tactical layer suppression and muting policy:
  // Under LOADING: suppress old reference lines and entry band completely to prevent ghosting.
  // Under DISCONNECTED or STALE: render semi-transparent (muted) with pointer-events disabled.
  const activeReferenceLines = useMemo(() => {
    if (freshness.state === 'LOADING') return [];
    return tradeGeometry.referenceLines;
  }, [freshness.state, tradeGeometry.referenceLines]);

  const activeEntryBand = useMemo(() => {
    if (freshness.state === 'LOADING') return null;
    return tradeGeometry.entryBand;
  }, [freshness.state, tradeGeometry.entryBand]);

  const activeTdMarker = useMemo(() => {
    if (freshness.state === 'LOADING') return null;
    return tdMarker;
  }, [freshness.state, tdMarker]);

  const isLayerMuted = freshness.state === 'STALE' || freshness.state === 'DISCONNECTED';

  const getStatusColor = (state: string) => {
    switch (state) {
      case 'FRESH':
        return {
          bg: 'rgba(46, 117, 89, 0.2)',
          border: 'var(--color-up, #2e7559)',
          text: 'var(--color-up, #4ee39d)',
          label: '🟢 FRESH (实时新鲜)',
        };
      case 'STALE':
        return {
          bg: 'rgba(230, 162, 60, 0.18)',
          border: 'var(--color-warning, #e6a23c)',
          text: 'var(--color-warning, #f5c069)',
          label: '🟡 STALE (时效陈旧/时钟错位)',
        };
      case 'LOADING':
        return {
          bg: 'rgba(74, 144, 226, 0.18)',
          border: 'var(--color-info, #4a90e2)',
          text: 'var(--color-info, #7ab5f7)',
          label: '🔵 LOADING (正在加载)',
        };
      case 'DISCONNECTED':
        return {
          bg: 'rgba(201, 74, 74, 0.25)',
          border: 'var(--color-down, #c94a4a)',
          text: 'var(--color-down, #ff7b7b)',
          label: '🔴 DISCONNECTED (网络断流/报价严重滞后)',
        };
      case 'EMPTY':
      default:
        return {
          bg: 'rgba(100, 110, 105, 0.2)',
          border: 'var(--border-subtle, #33443a)',
          text: 'var(--text-muted, #8b9b90)',
          label: '⚪ EMPTY (无数据)',
        };
    }
  };

  const statusStyle = getStatusColor(freshness.state);

  return (
    <MarketValidationLensModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${symbol} 走势与决策透镜`}
      subtitle={`辅助交叉验证 (Vela Native Canvas2D) · ${activeTimeframe}`}
      lensId={`vela-lens-${symbol}`}
      headerActions={
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
          {/* Timeframe selector tabs */}
          {AVAILABLE_TIMEFRAMES.map(tf => (
            <button
              key={tf}
              type="button"
              data-testid={`lens-timeframe-btn-${tf}`}
              onClick={() => handleTimeframeChange(tf)}
              style={{
                backgroundColor:
                  activeTimeframe === tf ? 'var(--color-accent, #38ef7d)' : 'transparent',
                color: activeTimeframe === tf ? '#0f1411' : 'var(--text-secondary, #b4c2b9)',
                border:
                  activeTimeframe === tf
                    ? '1px solid var(--color-accent, #38ef7d)'
                    : '1px solid var(--border-subtle, #1f2b23)',
                borderRadius: '4px',
                padding: '2px 6px',
                fontSize: '11px',
                fontWeight: activeTimeframe === tf ? 700 : 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-mono, monospace)',
                transition: 'all 0.15s ease',
              }}
            >
              {tf}
            </button>
          ))}
        </div>
      }
    >
      <div
        data-testid="cross-validation-drawer-content"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          backgroundColor: 'var(--bg-canvas, #0f1411)',
        }}
      >
        {/* 1. Truthful Freshness State Machine Banner */}
        <div
          data-testid="lens-freshness-banner"
          style={{
            padding: '8px 14px',
            backgroundColor: statusStyle.bg,
            borderBottom: `1px solid ${statusStyle.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 700, color: statusStyle.text }}>
              {statusStyle.label}
            </span>
            <span
              style={{
                color: 'var(--text-muted, #8b9b90)',
                fontSize: '11px',
                fontFamily: 'var(--font-mono, monospace)',
              }}
            >
              原因: {freshness.reason}
            </span>
          </div>

          {blueprint?.close_time_utc && (
            <div
              style={{
                fontSize: '11px',
                color: 'var(--text-muted, #8b9b90)',
                fontFamily: 'var(--font-mono, monospace)',
              }}
            >
              决策锚点: {blueprint.close_time_utc}
            </div>
          )}
        </div>

        {/* 2. Tactical Metrics Bar (Entry, Stop, Target, TD9) */}
        <div
          data-testid="lens-metrics-bar"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '8px',
            padding: '10px 14px',
            backgroundColor: 'var(--bg-surface, #121915)',
            borderBottom: '1px solid var(--border-subtle, #1f2b23)',
            fontSize: '11px',
            opacity: isLayerMuted ? 0.7 : 1,
            pointerEvents: freshness.state === 'DISCONNECTED' ? 'none' : 'auto',
            transition: 'opacity 0.2s ease',
          }}
        >
          {/* Entry Zone */}
          <div>
            <div style={{ color: 'var(--text-muted, #8b9b90)', fontSize: '10px' }}>战术入场区间</div>
            <div
              style={{
                fontWeight: 600,
                color: 'var(--color-accent, #38ef7d)',
                fontFamily: 'var(--font-mono, monospace)',
              }}
            >
              {blueprint?.entry_low && blueprint?.entry_high
                ? `${formatFinancialPrice(blueprint.entry_low, symbol)} ~ ${formatFinancialPrice(blueprint.entry_high, symbol)}`
                : '--'}
            </div>
          </div>

          {/* Stop Loss */}
          <div>
            <div style={{ color: 'var(--text-muted, #8b9b90)', fontSize: '10px' }}>失效止损位 (SL)</div>
            <div
              style={{
                fontWeight: 600,
                color: 'var(--color-down, #ff7b7b)',
                fontFamily: 'var(--font-mono, monospace)',
              }}
            >
              {formatFinancialPrice(blueprint?.invalidation_stop, symbol)}
            </div>
          </div>

          {/* Target */}
          <div>
            <div style={{ color: 'var(--text-muted, #8b9b90)', fontSize: '10px' }}>目标止盈位 (TP)</div>
            <div
              style={{
                fontWeight: 600,
                color: 'var(--color-up, #4ee39d)',
                fontFamily: 'var(--font-mono, monospace)',
              }}
            >
              {formatFinancialPrice(blueprint?.target_take_profit, symbol)}
            </div>
          </div>

          {/* TD9 Setup Marker */}
          <div>
            <div style={{ color: 'var(--text-muted, #8b9b90)', fontSize: '10px' }}>DeMark TD9 锚点</div>
            <div
              style={{
                fontWeight: 600,
                color: activeTdMarker?.isAligned
                  ? activeTdMarker.direction === 'BUY'
                    ? 'var(--color-up, #4ee39d)'
                    : 'var(--color-down, #ff7b7b)'
                  : 'var(--text-muted, #8b9b90)',
                fontFamily: 'var(--font-mono, monospace)',
              }}
            >
              {activeTdMarker?.message || (freshness.state === 'LOADING' ? '加载中...' : '--')}
            </div>
          </div>
        </div>

        {/* 3. Pure Chart Core Viewport */}
        <div
          style={{
            flex: 1,
            position: 'relative',
            minHeight: 0,
            opacity: isLayerMuted ? 0.85 : 1,
            filter: isLayerMuted ? 'grayscale(0.15)' : 'none',
            pointerEvents: freshness.state === 'DISCONNECTED' ? 'none' : 'auto',
          }}
        >
          <MarketValidationChart
            candles={chartCandles}
            referenceLines={activeReferenceLines}
            entryBand={activeEntryBand}
            tdMarker={activeTdMarker}
            symbolLabel={symbol}
            timeframeLabel={activeTimeframe}
            height="100%"
            width="100%"
            formatPrice={(p) => formatFinancialPrice(p, symbol)}
          />
        </div>
      </div>
    </MarketValidationLensModal>
  );
};
